import type { EngineRequest, EngineStep } from './validate-api';
import type { ValidateTurn } from './validate-runs';

/**
 * Where a reply's time went, from the engine's own log timestamps — the
 * testing tool Observer's timeline (`apps/observer` Preview / flight strip),
 * drawn from the bot engine's logs rather than Langfuse spans, so it is the
 * same evidence the per-reply trace already reads.
 *
 * A step lasts from its first log line to the next step's (or to the end of
 * its request). That's when the engine moved on, not a measured span, so it is
 * close but not exact for a node that does nothing slow.
 */

export interface TimelineBar {
  step: EngineStep;
  /** Ms from the request's start. */
  startMs: number;
  durationMs: number;
}

export interface TimelinePass {
  requestId: string;
  /** Epoch ms the engine started this request. */
  at: number;
  totalMs: number;
  bars: TimelineBar[];
}

export const passOf = (req: EngineRequest): TimelinePass => {
  const end = req.endAt ?? req.at;
  const timed = req.steps.filter(
    (s): s is EngineStep & { at: number } => typeof s.at === 'number'
  );
  const bars = timed.map((step, i) => {
    const next = timed[i + 1]?.at ?? end;
    return {
      step,
      startMs: Math.max(0, step.at - req.at),
      durationMs: Math.max(0, next - step.at),
    };
  });
  return {
    requestId: req.requestId,
    at: req.at,
    totalMs: Math.max(0, end - req.at),
    bars,
  };
};

/** The slowest step across a reply's requests, if any took measurable time. */
export const slowestBar = (passes: TimelinePass[]): TimelineBar | null =>
  passes
    .flatMap((p) => p.bars)
    .reduce<TimelineBar | null>(
      (top, bar) =>
        bar.durationMs > 0 && (!top || bar.durationMs > top.durationMs)
          ? bar
          : top,
      null
    );

export interface ReplyTiming {
  /** The bot reply's index in the transcript. */
  index: number;
  /** What the customer waited, measured by the test (first frame back). */
  waitMs: number | null;
  /** Time the engine spent walking nodes for it. */
  engineMs: number;
  slowest: TimelineBar | null;
  passes: TimelinePass[];
}

/** Each traced bot reply's wait, engine time and slowest step, in order. */
export const replyTimings = (
  transcript: ValidateTurn[],
  byReply: Record<string, EngineRequest[]>
): ReplyTiming[] =>
  transcript.flatMap((turn, index) => {
    const requests = byReply[String(index)];
    if (turn.role !== 'bot' || !requests?.length) return [];
    const passes = requests.map(passOf);
    return [
      {
        index,
        waitMs: typeof turn.latencyMs === 'number' ? turn.latencyMs : null,
        engineMs: passes.reduce((sum, p) => sum + p.totalMs, 0),
        slowest: slowestBar(passes),
        passes,
      },
    ];
  });

/** "340 ms", "1.24 s". */
export const formatMs = (ms: number): string =>
  ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(2)} s`;
